import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CustomerAccount } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

import { MAXIMUM_3A_CONTRIBUTION } from '@app/app.constants';
import { FinancialSituationBankDetail } from '@shared/models/Account';
import { NewDocType } from '@shared/models/Profile';
import { SharedService } from '@shared/services/shared.service';
import { ToastNotificationService } from '@shared/services/toast-notification.service';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-fund-invest-details',
  templateUrl: './fund-invest-details.component.html',
  styleUrls: ['./fund-invest-details.component.scss'],
  standalone: false
})
export class FundInvestDetailsComponent implements OnInit, AfterViewInit {
  @Input() appLocation: string;
  @Input() currentAccount: CustomerAccount;
  @Input() ownerName: string;
  @Input() transfer3ADoc: NewDocType[];
  @Input() financialDetails: null | { iban: number; bank: FinancialSituationBankDetail };
  @Output() fetchFinancialSituation: EventEmitter<void> = new EventEmitter<void>();
  @Output() getTransfer3ADoc: EventEmitter<void> = new EventEmitter();

  showDetails = false;
  isAndroid = false;
  qrDetailsBtnHeight: number;
  qrWrapHeight: number;
  isPossibleToCreate3A = false;
  stillPossibleToInvest: number;
  show3aTransferDetails = false;

  @ViewChild('fundQrWrap', { static: false }) qrWrap: ElementRef;
  @ViewChild('fundQrDetails', { static: false }) qrDetails: ElementRef;

  constructor(
    private translateService: TranslateService,
    private trackingService: TrackingService,
    private toastNotificationService: ToastNotificationService,
    private router: Router,
    private sharedService: SharedService
  ) {}

  ngOnInit() {
    this.isAndroid = navigator.userAgent.toLowerCase().indexOf('android') > -1;

    if (this.appLocation === 'de' && this.financialDetails === null) {
      this.fetchFinancialSituation.emit();
    }

    if (this.currentAccount && this.currentAccount.kind === '3a') {
      this.isPossibleToCreate3A = this.currentAccount.max_contribution_level !== 'transfer_only';
      const possibleToInvest =
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        MAXIMUM_3A_CONTRIBUTION[new Date().getFullYear()][this.currentAccount.max_contribution_level] - this.currentAccount.deposit_this_year;
      this.stillPossibleToInvest = possibleToInvest < 0 ? 0 : possibleToInvest;
    }
  }

  ngAfterViewInit() {
    if (window.innerWidth >= 580) {
      this.getContainersHeight();
    }
  }

  @HostListener('window:resize') onResize(): void {
    const { innerWidth } = window;

    if (innerWidth < 580) {
      if (this.showDetails) {
        this.showDetails = false;
        this.removeStyles();
      }
    }

    if (innerWidth >= 580) {
      if (typeof this.qrDetailsBtnHeight === 'undefined') {
        this.getContainersHeight();
      }
    }
  }

  toggleDetails() {
    this.showDetails = !this.showDetails;
    if (window.innerWidth >= 580) {
      if (this.showDetails) {
        this.qrWrap.nativeElement.style.flex = `0 0${this.qrWrap.nativeElement.offsetHeight}px`;
        this.qrWrap.nativeElement.style.maxHeight = `${this.qrWrap.nativeElement.offsetHeight}px`;
        this.qrDetails.nativeElement.style.flex = `1`;
        this.qrDetails.nativeElement.style.maxHeight = `none`;
      } else {
        this.removeStyles();
      }
    }
  }

  getContainersHeight() {
    if (!this.qrWrap || !this.qrDetails) {
      return;
    }
    this.qrWrapHeight = this.qrWrap.nativeElement.offsetHeight;
    this.qrDetailsBtnHeight = this.qrDetails.nativeElement.offsetHeight;
  }

  removeStyles() {
    if (!this.qrWrap || !this.qrDetails) {
      return;
    }
    this.qrWrap.nativeElement.style.removeProperty('flex');
    this.qrWrap.nativeElement.style.removeProperty('max-height');
    this.qrDetails.nativeElement.style.removeProperty('flex');
    this.qrDetails.nativeElement.style.removeProperty('max-height');
  }

  copyInformation(value: string) {
    this.trackingService.trackActivity(`[Button] Copy bank details information`);

    let copyValue = '';
    switch (value) {
      case 'address':
        copyValue = this.translateService.instant('reinvest.bankAddress');
        break;
      case 'swift':
        copyValue = this.translateService.instant('steps.id5.saxo_funded.swiftBic');
        break;
      case 'hypiAddress':
        copyValue = this.translateService.instant('sp.accountDetails2.qr.3a.paymentForAddress').replace(/<[^>]+>/g, '');
        break;
      case 'libertyAddress':
        copyValue = this.translateService.instant('sp.accountDetails2.qr.3a.inFavourAddress').replace(/<[^>]+>/g, '');
        break;
      default:
        copyValue = value;
        break;
    }

    const textarea = document.createElement('textarea');
    textarea.textContent = copyValue;
    textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in Microsoft Edge.
    document.body.appendChild(textarea);
    textarea.select();
    try {
      const success = document.execCommand('copy'); // Security exception may be thrown by some browsers.
      if (success) {
        this.toastNotificationService.show('success', this.translateService.instant('sp.revamp.accountDetails.copied') as string);
      }
    } catch (ex) {
      window.prompt('Copy to clipboard: Ctrl+C, Enter', copyValue);
    } finally {
      document.body.removeChild(textarea);
    }
  }

  toggle3aTransferDetails() {
    this.show3aTransferDetails = !this.show3aTransferDetails;
    if (this.show3aTransferDetails && this.transfer3ADoc.length === 0) {
      this.getTransfer3ADoc.emit();
    }
  }

  onDownloadForm() {
    if (this.transfer3ADoc.length === 0) {
      return;
    }
    this.sharedService.downloadCustomerDocuments(
      this.transfer3ADoc[0].attributes.translated_title,
      this.transfer3ADoc[0].attributes.type_id,
      this.transfer3ADoc[0].id
    );
  }

  onOpenHelp() {
    this.trackingService.trackActivity('[Link] Deposit delay info: Open help');
    void this.router.navigate(['/help']);
  }
}
